<template>
  <canvas
    v-show="playing"
    ref="rtspCanvas"
    :id="rtspCanvasId"
    class="rtsp-video-player w-full"
    :class="videoCanvasClass"
    data-before-destroy-triggered="false"
  ></canvas>
</template>

<script>
import Vue from "vue";
import { loadPlayer } from "rtsp-relay/browser";
const CLEANUP_INTERVAL_MILLISECONDS = 1000;

/**
 * RtspVideoPlayer: RTSPストリームを再生するためのビデオプレーヤー。
 *
 * @component RtspVideoPlayer
 * @props {string} rtspCanvasId - RTSPストリームを表示するcanvasのID
 * @props {string} videoCanvasClass - RTSPストリームを表示するcanvasのクラス
 * @props {string} rtspSrc - 再生するRTSPストリームのURL(wss想定)
 * @props {number} jsPegReconnectIntervalSeconds - RTSPプレーヤーが内部的に使用するwebsocketの接続試行間隔（秒）
 * @props {number} connectRetryDelayMilliseconds - RTSPプレーヤーの初期化時にエラーがあった場合に再接続を試みる間隔（ミリ秒）
 * @props {number} disconnectDetectionMilliSeconds - 切断検出の間隔（ミリ秒）。動画デコード時にこの間隔で切断検出を行う
 */
export default {
  name: "RtspVideoPlayer",
  props: {
    rtspCanvasId: { type: String, required: true },
    videoCanvasClass: { type: String, default: "video-canvas" },
    rtspSrc: { type: String, required: true },
    jsPegReconnectIntervalSeconds: { type: Number, default: 5 },
    connectRetryDelayMilliseconds: { type: Number, default: 1000 },
    disconnectDetectionMilliSeconds: { type: Number, default: 5000 },
  },
  data() {
    return {
      rtspPlayer: null,
      playStart: false,
      playing: false,
      reconnectIntervalId: null, // number | null
      disconnectCheckId: null, // number | null
    };
  },

  methods: {
    isRtspPlayerExists() {
      return this.rtspPlayer !== null;
    },

    destroyRtspConnection() {
      if (this.isRtspPlayerExists()) {
        this.rtspPlayer.destroy();
        this.rtspPlayer = null;
      }
    },

    cleanup() {
      clearInterval(this.reconnectIntervalId);
      clearTimeout(this.disconnectCheckId);
      this.destroyRtspConnection();
    },

    async playRtspVideo() {
      if (this.playStart) return;
      if (this.isRtspPlayerExists()) return;
      try {
        this.playStart = true;
        this.$emit("playStart");
        this.rtspPlayer = await loadPlayer({
          url: this.rtspSrc,
          canvas: this.$refs.rtspCanvas,
          reconnectInterval: this.jsPegReconnectIntervalSeconds, // websocketの接続試行間隔
          onVideoDecode: () => {
            clearTimeout(this.disconnectCheckId);
            this.$emit("connectionRestored");
            this.disconnectCheckId = setTimeout(() => {
              this.$emit("disconnectDetection");
            }, this.disconnectDetectionMilliSeconds);
            if (!this.playing) {
              // 動画がrenderingされたら再生中とみなす
              clearInterval(this.reconnectIntervalId);
              this.playing = true;
              this.$emit("playing");
            }
          },
        });
      } catch (error) {
        this.handlePlayError(error);
      } finally {
        this.playStart = false;
      }
    },

    async handlePlayError(error) {
      this.playing = false;
      setTimeout(this.reconnect); // 接続失敗時は再接続を試みる
      Vue.$log.debug(`Error playVideo :`, error);
      this.$emit("playError");
    },

    async reconnect(delay = this.connectRetryDelayMilliseconds) {
      if (this.reconnectIntervalId) {
        clearInterval(this.reconnectIntervalId);
      }
      this.reconnectIntervalId = setInterval(async () => {
        if (this.playStart) return;
        this.destroyRtspConnection();
        await this.playRtspVideo();
        clearInterval(this.reconnectIntervalId);
      }, delay);
    },

    async setRtspPlayerInstanceAndModifyCanvas() {
      // NOTE: 接続完了していない状態の時にrtspPlayerインスタンスをdataにセットする関数
      // rtsp-relay/browserの仕様から、canvasのattribute変更が行われるまでloadPlayer()の返り値が未解決となるが
      // 通常だと接続完了しないとcanvasのattribute変更が起きないため、手動で変更する
      // dataの変更だとDOMの変更がすぐに起きずbeforeDestroy時に検知できない場合があったため、attributeを直接変更する
      this.$refs.rtspCanvas.setAttribute(
        "data-before-destroy-triggered",
        "true",
      );
      await this.$nextTick();
    },
  },

  async mounted() {
    this.$nextTick(async () => {
      this.playRtspVideo();
    });
  },

  async beforeDestroy() {
    await this.setRtspPlayerInstanceAndModifyCanvas();
    const cleanupInterval = setInterval(() => {
      this.cleanup();
      clearInterval(cleanupInterval);
    }, CLEANUP_INTERVAL_MILLISECONDS);
  },
};
</script>

<style lang="scss" scoped>
.video-canvas {
  width: 100%;
  background-color: #222222;
  border-radius: 5px;
  object-fit: contain;
}
</style>
